import React, { useState, useEffect } from 'react';
import useFormDraft from '../hooks/useFormDraft';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Box,
  TextField,
  Grid,
  Autocomplete,
  IconButton,
  Typography,
  Divider,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { Article } from '../types';
import { Client } from '../types/client';
import { Demandeur } from '../types/demandeur';
import { Service } from '../types/service';
import { Projet } from '../types/projet';
import { StockExitNewCreate, StockExitNew, StockExitItemCreate } from '../types/stockExitNew';
import {
  stockExitsNewAPI,
  stockEntriesAPI,
  getCurrentStock,
} from '../services/api';
import { extractErrorMessage } from '../utils/errorHandler';
import ConfirmationDialog, { ConfirmationField } from './ConfirmationDialog';
import { SlideUpTransition, useMinimizable, DialogControls, MinimizedBar } from './MinimizableDialog';
import PriceInput, { parsePriceValue } from './PriceInput';

interface StockExitNewAddProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  articles: Article[];
  clients: Client[];
  demandeurs: Demandeur[];
  services: Service[];
  projets: Projet[];
  showError: (message: string) => void;
  showSuccess: (message: string) => void;
  minimized?: boolean;
  onMinimizedChange?: (minimized: boolean) => void;
}

/** Above this margin a line gets a warning (not a block): usually a typo in the price. */
const MARGE_ALERTE = 300;
const margeElevee = (m: number | string | null) =>
  (typeof m === 'string' ? parseFloat(m.replace(',', '.')) : m ?? 0) > MARGE_ALERTE;

interface ArticleLineItem extends Omit<StockExitItemCreate, 'quantity' | 'prix_entree' | 'marge'> {
  availableStock?: number;
  loadingStock?: boolean;
  stockWarning?: string;
  article?: Article;
  quantity: number | null | string;
  prix_entree: number | null | string;
  marge: number | null | string;
  prix_sortie: number | null | string;
}

const StockExitNewAdd: React.FC<StockExitNewAddProps> = ({
  open,
  onClose,
  onSuccess,
  articles,
  clients,
  demandeurs,
  services,
  projets,
  showError,
  showSuccess,
  minimized: controlledMinimized,
  onMinimizedChange,
}) => {
  const { minimized, setMinimized, minimize, restore, dialogOpen } = useMinimizable(
    open,
    controlledMinimized,
    onMinimizedChange
  );

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    bon_number: '',
    is_paid: false,
    client_id: null as number | null,
    client_code: '',
    demandeur_id: null as number | null,
    service_id: null as number | null,
    projet_id: null as number | null,
    advance: null as number | null | string,
  });

  const [articleLines, setArticleLines] = useState<ArticleLineItem[]>([
    {
      article_id: 0,
      quantity: null,
      prix_entree: null,
      marge: null,
      prix_sortie: null,
    },
  ]);

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [createdResponse, setCreatedResponse] = useState<StockExitNew | null>(null);

  // Cache the in-progress form so an accidental backdrop-close doesn't wipe it.
  const { saveDraft, loadDraft, clearDraft } = useFormDraft<{
    formData: typeof formData;
    articleLines: ArticleLineItem[];
  }>('stock-exit');

  // Restore a cached draft when the dialog is (re)opened.
  useEffect(() => {
    if (open) {
      const draft = loadDraft();
      if (draft) {
        setFormData(draft.formData);
        setArticleLines(draft.articleLines);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Auto-generate unique BON number when dialog opens
  useEffect(() => {
    const generateBonNumber = async () => {
      if (open) {
        try {
          // Use backend endpoint to get next BON number (includes deleted records)
          const response = await stockExitsNewAPI.getNextBonNumber();
          setFormData((prev) => ({
            ...prev,
            bon_number: response.bon_number,
          }));
        } catch (error) {
          console.error('Error generating BON number:', error);
          // Fallback to year-based BON number
          const year = new Date().getFullYear().toString().slice(-2);
          const bonNumber = `BN-${year}-001`;
          setFormData((prev) => ({
            ...prev,
            bon_number: bonNumber,
          }));
        }
      }
    };

    generateBonNumber();
  }, [open]);

  const formatNumber = (value: any, decimals: number = 2): string => {
    return Number(value || 0).toFixed(decimals);
  };

  const handleAddArticleLine = () => {
    setArticleLines([
      ...articleLines,
      {
        article_id: 0,
        quantity: null,
        prix_entree: null,
        marge: null,
        prix_sortie: null,
      },
    ]);
  };

  const handleRemoveArticleLine = (index: number) => {
    if (articleLines.length > 1) {
      setArticleLines(articleLines.filter((_, i) => i !== index));
    }
  };

  const handleArticleChange = async (index: number, article: Article | null) => {
    const newLines = [...articleLines];

    if (article) {
      // Set loading state
      newLines[index].loadingStock = true;
      newLines[index].stockWarning = undefined;
      setArticleLines([...newLines]);

      let stockWarning: string | undefined = undefined;
      let availableStock: number | undefined = undefined;

      // Fetch current stock availability
      try {
        const stockInfo = await getCurrentStock(article.id);
        const currentStock = stockInfo.stock_final || 0;
        availableStock = currentStock;

        // Show warning if stock is low or zero
        if (currentStock <= 0) {
          stockWarning = `Stock épuisé (Stock: ${currentStock})`;
        } else if (currentStock <= (article.seuil_alerte || 0)) {
          stockWarning = `Stock faible (Disponible: ${currentStock})`;
        }
      } catch (error) {
        console.error('Error fetching current stock:', error);
        stockWarning = 'Impossible de vérifier le stock';
      }

      // Fetch the maximum unit price for this article using the efficient endpoint
      let maxUnitPrice = 0;
      try {
        const response = await stockEntriesAPI.getArticleMaxPrice(article.id);
        maxUnitPrice = response.max_price;
      } catch (error) {
        console.error('Error fetching article max price:', error);
      }

      // Update all properties at once
      const updatedLines = [...articleLines];
      const margeValue = updatedLines[index].marge;
      const currentMarge = typeof margeValue === 'string'
        ? parseFloat(margeValue.replace(',', '.')) || 0
        : (margeValue ?? 0);
      updatedLines[index] = {
        ...updatedLines[index],
        article_id: article.id,
        article: article,
        prix_entree: Number(maxUnitPrice),
        prix_sortie: Math.round(Number(maxUnitPrice) * (1 + (currentMarge as number) / 100) * 100) / 100,
        availableStock: availableStock,
        stockWarning: stockWarning,
        loadingStock: false,
      };

      setArticleLines(updatedLines);
    } else {
      // Clear the line
      const updatedLines = [...articleLines];
      updatedLines[index] = {
        article_id: 0,
        quantity: null,
        prix_entree: null,
        marge: null,
        prix_sortie: null,
      };
      setArticleLines(updatedLines);
    }
  };

  const handleArticleFieldChange = (
    index: number,
    field: keyof ArticleLineItem,
    value: any
  ) => {
    const newLines = [...articleLines];

    newLines[index] = {
      ...newLines[index],
      [field]: value,
    };

    const prixEntreeValue = newLines[index].prix_entree;
    const prixEntree = typeof prixEntreeValue === 'string'
      ? parseFloat(prixEntreeValue.replace(',', '.')) || 0
      : (prixEntreeValue ?? 0);

    const quantityValue = newLines[index].quantity;
    const quantity = typeof quantityValue === 'string'
      ? parseFloat(quantityValue.replace(',', '.')) || 0
      : (quantityValue ?? 0);

    // Check if quantity exceeds available stock
    if (field === 'quantity' && newLines[index].availableStock !== undefined) {
      const availableStock = newLines[index].availableStock!;
      if (quantity > availableStock) {
        newLines[index].stockWarning = `Quantité (${quantity}) > Stock (${availableStock})`;
      } else if (quantity <= availableStock && newLines[index].stockWarning?.includes('>')) {
        const article = newLines[index].article;
        if (article && availableStock <= (article.seuil_alerte || 0)) {
          newLines[index].stockWarning = `Stock faible (Disponible: ${availableStock})`;
        } else if (availableStock > 0) {
          newLines[index].stockWarning = undefined;
        }
      }
    }

    // Auto-calculate prix_sortie when marge or prix_entree changes
    if (field === 'marge' || field === 'prix_entree') {
      const margeValue = newLines[index].marge;
      const marge = typeof margeValue === 'string'
        ? parseFloat(margeValue.replace(',', '.')) || 0
        : (margeValue ?? 0);

      // Calculate PRIX S = PRIX E × (1 + marge/100)
      const prixSortie = Math.round((prixEntree as number) * (1 + (marge as number) / 100) * 100) / 100;
      newLines[index].prix_sortie = prixSortie;
    }

    // When user manually edits prix_sortie, recalculate marge based on prix_entree
    if (field === 'prix_sortie' && prixEntree > 0) {
      const prixSortieValue = newLines[index].prix_sortie;
      const prixSortie = typeof prixSortieValue === 'string'
        ? parseFloat(prixSortieValue.replace(',', '.')) || 0
        : (prixSortieValue ?? 0);

      // Calculate marge = ((prix_sortie / prix_entree) - 1) * 100
      const calculatedMarge = Math.round(((prixSortie / prixEntree) - 1) * 100 * 100) / 100;
      newLines[index].marge = calculatedMarge;
    }

    setArticleLines(newLines);
  };

  const handleClientChange = (clientId: number | null) => {
    const selectedClient = clients.find((client) => client.id === clientId);
    const isMAF = selectedClient?.code === 'CLI-001';

    setFormData({
      ...formData,
      client_id: clientId,
      client_code: selectedClient ? selectedClient.code : '',
      demandeur_id: isMAF ? formData.demandeur_id : null,
      service_id: isMAF ? formData.service_id : null,
      projet_id: isMAF ? formData.projet_id : null,
    });
  };

  const calculateGrandTotal = (): number => {
    return articleLines.reduce((total, line) => {
      const quantity = typeof line.quantity === 'string'
        ? parseFloat(line.quantity.replace(',', '.')) || 0
        : (line.quantity ?? 0);
      const prixSortie = typeof line.prix_sortie === 'string'
        ? parseFloat(line.prix_sortie.replace(',', '.')) || 0
        : (line.prix_sortie ?? 0);
      const lineTotal = quantity * prixSortie;
      return total + lineTotal;
    }, 0);
  };

  const handleSubmitClick = () => {
    // Validate all required fields
    if (!formData.bon_number || !formData.bon_number.trim()) {
      showError('Veuillez saisir le numéro de bon');
      return;
    }

    if (!formData.client_id) {
      showError('Veuillez sélectionner un client');
      return;
    }

    const isMAF = formData.client_code === 'CLI-001';

    if (isMAF && !formData.demandeur_id) {
      showError('Veuillez sélectionner un demandeur');
      return;
    }

    if (isMAF && !formData.service_id) {
      showError('Veuillez sélectionner un service');
      return;
    }

    if (isMAF && !formData.projet_id) {
      showError('Veuillez sélectionner un projet');
      return;
    }

    // Validate article lines
    for (let i = 0; i < articleLines.length; i++) {
      const line = articleLines[i];

      if (!line.article_id || line.article_id === 0) {
        showError(`Veuillez sélectionner un article pour la ligne ${i + 1}`);
        return;
      }

      const quantity = typeof line.quantity === 'string'
        ? parseFloat(line.quantity.replace(',', '.'))
        : line.quantity;

      if (quantity === null || isNaN(quantity as number) || (quantity as number) <= 0) {
        showError(`La quantité doit être supérieure à 0 pour la ligne ${i + 1}`);
        return;
      }

      if (line.availableStock !== undefined && (quantity as number) > line.availableStock) {
        showError(
          `Stock insuffisant pour la ligne ${i + 1}! Quantité demandée: ${quantity}, Stock disponible: ${line.availableStock}`
        );
        return;
      }

      if (line.availableStock !== undefined && line.availableStock <= 0) {
        showError(
          `Impossible de créer une sortie: le stock est épuisé pour la ligne ${i + 1} (Stock: ${line.availableStock})`
        );
        return;
      }
    }

    setShowConfirmDialog(true);
  };

  const handleConfirmSubmit = async () => {
    setIsSubmitting(true);

    try {
      const createData: StockExitNewCreate = {
        date: formData.date || new Date().toISOString().split('T')[0],
        bon_number: formData.bon_number,
        is_paid: formData.is_paid,
        advance: parsePriceValue(formData.advance),
        client_id: formData.client_id,
        client_code: formData.client_code || null,
        demandeur_id: formData.demandeur_id,
        service_id: formData.service_id,
        projet_id: formData.projet_id,
        items: articleLines.map((line) => {
          const quantity = typeof line.quantity === 'string'
            ? parseFloat(line.quantity.replace(',', '.'))
            : (line.quantity ?? 0);
          const prixEntree = typeof line.prix_entree === 'string'
            ? parseFloat(line.prix_entree.replace(',', '.'))
            : (line.prix_entree ?? 0);
          const marge = typeof line.marge === 'string'
            ? parseFloat(line.marge.replace(',', '.'))
            : (line.marge ?? 0);
          return {
            article_id: line.article_id,
            quantity: Math.round(quantity * 100) / 100,
            prix_entree: Math.round(prixEntree * 100) / 100,
            marge: Math.round(marge * 100) / 100,
          };
        }),
      };

      const result = await stockExitsNewAPI.create(createData);
      setCreatedResponse(result);
      setShowConfirmDialog(false);
      setShowSuccessDialog(true);
      showSuccess(
        `Sortie de stock créée avec succès! ${result.total_articles} article(s), Total: ${result.grand_total} MAD`
      );
      clearDraft();
      onSuccess();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getConfirmationFields = (): ConfirmationField[] => {
    const fields: ConfirmationField[] = [];

    fields.push({ label: 'Date', value: formData.date });
    fields.push({ label: 'BON N°', value: formData.bon_number });

    if (formData.client_id) {
      const client = clients.find((c) => c.id === formData.client_id);
      if (client) {
        fields.push({ label: 'Client', value: `${client.code} - ${client.name}` });
      }
    }

    if (formData.demandeur_id) {
      const demandeur = demandeurs.find((d) => d.id === formData.demandeur_id);
      if (demandeur) {
        fields.push({ label: 'Demandeur', value: `${demandeur.prenom} ${demandeur.nom}` });
      }
    }

    if (formData.service_id) {
      const service = services.find((s) => s.id === formData.service_id);
      if (service) {
        fields.push({ label: 'Service', value: service.name });
      }
    }

    if (formData.projet_id) {
      const projet = projets.find((p) => p.id === formData.projet_id);
      if (projet) {
        fields.push({ label: 'Projet', value: `${projet.code} - ${projet.nom}` });
      }
    }

    fields.push({ label: '---', value: '---' });
    fields.push({ label: 'Articles', value: `${articleLines.length}`, bold: true });

    articleLines.forEach((line, index) => {
      const article = articles.find((a) => a.id === line.article_id);
      if (article) {
        const quantity = typeof line.quantity === 'string'
          ? parseFloat(line.quantity.replace(',', '.')) || 0
          : (line.quantity ?? 0);
        const prixSortie = typeof line.prix_sortie === 'string'
          ? parseFloat(line.prix_sortie.replace(',', '.')) || 0
          : (line.prix_sortie ?? 0);

        fields.push({
          label: `Article ${index + 1}`,
          value: `${article.reference} - ${article.designation}`,
        });
        fields.push({ label: `  Quantité`, value: `${quantity}` });
        fields.push({ label: `  Prix Sortie`, value: `${formatNumber(prixSortie)} MAD` });
        fields.push({ label: `  Total`, value: `${formatNumber(quantity * prixSortie)} MAD` });
      }
    });

    fields.push({ label: '---', value: '---' });
    fields.push({
      label: 'TOTAL GÉNÉRAL',
      value: `${formatNumber(calculateGrandTotal())} MAD`,
      bold: true,
    });

    return fields;
  };

  const handleSuccessDialogClose = () => {
    setShowSuccessDialog(false);
    setCreatedResponse(null);
    handleClose();
  };

  // Annuler = deliberate cancel: wipe inputs and discard any draft.
  const handleClose = () => {
    clearDraft();
    setFormData({
      date: new Date().toISOString().split('T')[0],
      bon_number: '',
      is_paid: false,
      client_id: null,
      client_code: '',
      demandeur_id: null,
      service_id: null,
      projet_id: null,
      advance: null,
    });
    setArticleLines([
      {
        article_id: 0,
        quantity: null,
        prix_entree: null,
        marge: null,
        prix_sortie: null,
      },
    ]);
    setShowConfirmDialog(false);
    setIsSubmitting(false);
    setMinimized(false);
    onClose();
  };

  // Backdrop click / Escape = accidental: keep the work as a draft, then close.
  const handleDialogClose = (
    _event: object,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      saveDraft({ formData, articleLines });
      onClose();
    }
  };

  return (
    <>
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="lg" fullWidth TransitionComponent={SlideUpTransition}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Nouvelle sortie de stock
          <DialogControls color="#2196F3" onMinimize={minimize} onClose={handleClose} />
        </DialogTitle>
        <DialogContent>
          {/* Date and BON Number */}
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="BON N°"
                  value={formData.bon_number}
                  fullWidth
                  required
                  disabled
                  InputProps={{ readOnly: true }}
                  sx={{
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: '#000000',
                      color: '#000000',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.is_paid}
                      onChange={(e) => setFormData({ ...formData, is_paid: e.target.checked })}
                      color="primary"
                    />
                  }
                  label="Payé"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <PriceInput
                  label="Avance (acompte)"
                  value={formData.advance}
                  onChange={(v) => setFormData({ ...formData, advance: v })}
                  variant="outlined"
                  size="medium"
                  helperText="Avance payée à la création"
                />
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Article Lines Section */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Articles</Typography>
              <IconButton
                color="primary"
                size="small"
                onClick={handleAddArticleLine}
                sx={{ border: '2px solid', borderColor: 'primary.main' }}
              >
                <AddIcon />
              </IconButton>
            </Box>

            {articleLines.map((line, index) => (
              <Box key={index} sx={{ mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <Autocomplete
                      options={articles}
                      getOptionLabel={(option) => `${option.reference} - ${option.designation}`}
                      value={articles.find((a) => a.id === line.article_id) || null}
                      onChange={(_, value) => handleArticleChange(index, value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Article"
                          placeholder="Sélectionner article"
                          size="small"
                          required
                        />
                      )}
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={6} sm={3} md={2}>
                    <TextField
                      label="Quantité"
                      type="text"
                      value={line.quantity ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          handleArticleFieldChange(index, 'quantity', null);
                        } else if (/^\d*[.,]?\d*$/.test(value)) {
                          handleArticleFieldChange(index, 'quantity', value);
                        }
                      }}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value && value !== '') {
                          const numValue = parseFloat(value.replace(',', '.'));
                          if (!isNaN(numValue)) {
                            handleArticleFieldChange(index, 'quantity', numValue);
                          } else {
                            handleArticleFieldChange(index, 'quantity', null);
                          }
                        }
                      }}
                      size="small"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={6} sm={3} md={2}>
                    <TextField
                      label="Prix MAX"
                      type="text"
                      value={line.prix_entree ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          handleArticleFieldChange(index, 'prix_entree', null);
                        } else if (/^\d*[.,]?\d*$/.test(value)) {
                          handleArticleFieldChange(index, 'prix_entree', value);
                        }
                      }}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value && value !== '') {
                          const numValue = parseFloat(value.replace(',', '.'));
                          if (!isNaN(numValue)) {
                            handleArticleFieldChange(index, 'prix_entree', numValue);
                          } else {
                            handleArticleFieldChange(index, 'prix_entree', null);
                          }
                        }
                      }}
                      size="small"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={6} sm={3} md={2}>
                    <TextField
                      label="Marge %"
                      type="text"
                      value={line.marge ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          handleArticleFieldChange(index, 'marge', null);
                        } else if (/^\d*[.,]?\d*$/.test(value)) {
                          handleArticleFieldChange(index, 'marge', value);
                        }
                      }}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value && value !== '') {
                          const numValue = parseFloat(value.replace(',', '.'));
                          if (!isNaN(numValue)) {
                            handleArticleFieldChange(index, 'marge', numValue);
                          } else {
                            handleArticleFieldChange(index, 'marge', null);
                          }
                        }
                      }}
                      helperText={margeElevee(line.marge) ? 'Marge élevée : vérifiez le prix' : undefined}
                      FormHelperTextProps={{ sx: { color: 'warning.main', mx: 0 } }}
                      size="small"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={6} sm={3} md={2}>
                    <TextField
                      label="Prix S"
                      type="text"
                      value={line.prix_sortie ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          handleArticleFieldChange(index, 'prix_sortie', null);
                        } else if (/^\d*[.,]?\d*$/.test(value)) {
                          handleArticleFieldChange(index, 'prix_sortie', value);
                        }
                      }}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value && value !== '') {
                          const numValue = parseFloat(value.replace(',', '.'));
                          if (!isNaN(numValue)) {
                            handleArticleFieldChange(index, 'prix_sortie', numValue);
                          } else {
                            handleArticleFieldChange(index, 'prix_sortie', null);
                          }
                        }
                      }}
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} sm="auto" md="auto" sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => handleRemoveArticleLine(index)}
                      disabled={articleLines.length === 1}
                      sx={{
                        border: '1px solid',
                        borderColor: articleLines.length === 1 ? 'grey.300' : 'error.main',
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>

                  {/* Stock warning */}
                  {line.stockWarning && (
                    <Grid item xs={12}>
                      <Alert
                        severity={line.availableStock !== undefined && line.availableStock <= 0 ? 'error' : 'warning'}
                        icon={<WarningIcon />}
                        sx={{ py: 0 }}
                      >
                        {line.stockWarning}
                        {line.availableStock !== undefined && ` (Stock: ${line.availableStock})`}
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              </Box>
            ))}
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Client, Demandeur, Service, Projet Fields */}
          <Box sx={{ mb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={formData.client_code === 'CLI-001' ? 6 : 12}>
                <Autocomplete
                  options={clients}
                  getOptionLabel={(option) => `${option.code} - ${option.name}`}
                  value={clients.find((c) => c.id === formData.client_id) || null}
                  onChange={(_, value) => handleClientChange(value?.id || null)}
                  renderInput={(params) => <TextField {...params} label="Client" required />}
                />
              </Grid>

              {formData.client_code === 'CLI-001' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      options={demandeurs}
                      getOptionLabel={(option) => `${option.prenom} ${option.nom}`}
                      value={demandeurs.find((d) => d.id === formData.demandeur_id) || null}
                      onChange={(_, value) => setFormData({ ...formData, demandeur_id: value?.id || null })}
                      renderInput={(params) => <TextField {...params} label="Demandeur" required />}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      options={services}
                      getOptionLabel={(option) => option.name}
                      value={services.find((s) => s.id === formData.service_id) || null}
                      onChange={(_, value) => setFormData({ ...formData, service_id: value?.id || null })}
                      renderInput={(params) => <TextField {...params} label="Service" required />}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      options={projets}
                      getOptionLabel={(option) => `${option.code} - ${option.nom}`}
                      value={projets.find((p) => p.id === formData.projet_id) || null}
                      onChange={(_, value) => setFormData({ ...formData, projet_id: value?.id || null })}
                      renderInput={(params) => <TextField {...params} label="Projet" required />}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Annuler</Button>
          <Button onClick={handleSubmitClick} variant="contained">
            Créer
          </Button>
        </DialogActions>
      </Dialog>

      <MinimizedBar
        open={open && minimized}
        color="#2196F3"
        title="Nouvelle sortie de stock"
        onRestore={restore}
        onClose={handleClose}
      />

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={showConfirmDialog}
        title="Confirmer la sortie de stock"
        message="Veuillez vérifier les informations avant de confirmer la sortie de stock."
        fields={getConfirmationFields()}
        movementType="exit"
        onConfirm={handleConfirmSubmit}
        onCancel={() => setShowConfirmDialog(false)}
        confirmText="Confirmer la sortie"
        cancelText="Annuler"
        loading={isSubmitting}
      />

      {/* Success Dialog */}
      <Dialog open={showSuccessDialog} onClose={handleSuccessDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Sortie de stock créée avec succès</DialogTitle>
        <DialogContent>
          {createdResponse && (
            <div>
              <p>BON N°: {createdResponse.bon_number}</p>
              <p>Nombre d'articles: {createdResponse.total_articles}</p>
              <p>Total: {formatNumber(createdResponse.grand_total)} MAD</p>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSuccessDialogClose} variant="contained">
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StockExitNewAdd;
